/**
 * useCompanyNotes — Main hook for the tabbed company notes system.
 *
 * Manages: data fetching, active tab state, tab CRUD, save-from-AI.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import * as notesApi from '../services/companyNotesService';

const DEFAULT_TABS = [
    { tabId: 'investment-thesis', tabName: 'Investment Thesis', order: 1, isDefault: true },
    { tabId: 'key-risks', tabName: 'Key Risks', order: 2, isDefault: true },
    { tabId: 'competitors', tabName: 'Competitors', order: 3, isDefault: true },
    { tabId: 'management', tabName: 'Management Quality', order: 4, isDefault: true },
    { tabId: 'financials', tabName: 'Financial Analysis', order: 5, isDefault: true },
];

function replaceImageSrcWithSignedUrls(html = '', imageUrlByAttachment = {}) {
    if (!html || typeof html !== 'string') return html || '';
    return html.replace(/<img\b[^>]*>/gi, (imgTag) => {
        const idMatch = imgTag.match(/data-attachment-id\s*=\s*['"]([^'"]+)['"]/i);
        if (!idMatch) return imgTag;
        const attachmentId = idMatch[1];
        const signedUrl = imageUrlByAttachment[attachmentId];
        if (!signedUrl) return imgTag;

        if (/\ssrc\s*=\s*['"][^'"]*['"]/i.test(imgTag)) {
            return imgTag.replace(/\ssrc\s*=\s*['"][^'"]*['"]/i, ` src="${signedUrl}"`);
        }
        return imgTag.replace('<img', `<img src="${signedUrl}"`);
    });
}

export default function useCompanyNotes(companyId, companyName) {
    const [notes, setNotes] = useState(null);             // full document
    const [tabs, setTabs] = useState([]);                  // ordered tab list
    const [activeTabId, setActiveTabId] = useState('investment-thesis');
    const [loading, setLoading] = useState(false);
    const [badge, setBadge] = useState(0);                 // tabs with content count
    const fetchedRef = useRef(null);                       // track which companyId was fetched

    /* ── Fetch full notes (lazy - only when explicitly called) ── */
    const fetchNotes = useCallback(async (force = false) => {
        if (!companyId) {
            console.log('[useCompanyNotes] No companyId provided, skipping fetch');
            return;
        }

        // Skip if already fetched for this company (unless forced)
        if (!force && fetchedRef.current === companyId) {
            console.log('[useCompanyNotes] Already fetched for this company');
            return;
        }

        fetchedRef.current = companyId;
        setLoading(true);
        try {
            const res = await notesApi.getCompanyNotes(companyId);
            console.log('[useCompanyNotes] API response:', res);
            const payload = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
            if (payload) {
                console.log('[useCompanyNotes] Setting notes data:', payload);
                console.log('[useCompanyNotes] Tabs:', payload.tabs);
                let imageUrlByAttachment = {};
                try {
                    const imageRes = await notesApi.listImages(companyId);
                    const imagesPayload = (imageRes && typeof imageRes === 'object' && 'data' in imageRes) ? imageRes.data : imageRes;
                    const images = imagesPayload || [];
                    imageUrlByAttachment = (images || []).reduce((acc, item) => {
                        if (item?.attachmentId && item?.viewUrl) {
                            acc[item.attachmentId] = item.viewUrl;
                        }
                        return acc;
                    }, {});
                } catch (imgErr) {
                    console.warn('[useCompanyNotes] listImages failed, continuing without signed image URLs:', imgErr);
                }

                const enrichedTabs = (payload.tabs || []).map((tab) => {
                    const existingContent = tab?.content || {};
                    const existingHtml = existingContent.richContent || '';
                    return {
                        ...tab,
                        content: {
                            ...existingContent,
                            richContent: replaceImageSrcWithSignedUrls(existingHtml, imageUrlByAttachment),
                        },
                    };
                });

                const hydratedData = { ...payload, tabs: enrichedTabs };
                setNotes(hydratedData);
                const sortedTabs = [...enrichedTabs].sort((a, b) => a.order - b.order);
                console.log('[useCompanyNotes] Sorted tabs:', sortedTabs);
                setTabs(sortedTabs);
                if (hydratedData.activeTabId) setActiveTabId(hydratedData.activeTabId);
                // Compute badge
                const count = sortedTabs.filter(
                    t => t.content?.plainText?.trim()
                ).length;
                setBadge(count);
            } else {
                // No notes yet — use defaults
                setNotes(null);
                setTabs(DEFAULT_TABS.map(t => ({ ...t, content: { title: '', richContent: '', plainText: '', lastEdited: null } })));
                setActiveTabId('investment-thesis');
                setBadge(0);
            }
        } catch (err) {
            console.error('[useCompanyNotes] fetch error:', err);
            // Gracefully handle error - use default tabs
            setNotes(null);
            setTabs(DEFAULT_TABS.map(t => ({ ...t, content: { title: '', richContent: '', plainText: '', lastEdited: null } })));
            setActiveTabId('investment-thesis');
            setBadge(0);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    /* ── Fetch badge only (lightweight) ── */
    const fetchBadge = useCallback(async () => {
        if (!companyId) {
            console.log('[useCompanyNotes] No companyId for badge fetch');
            return;
        }
        try {
            const res = await notesApi.getNoteSummary(companyId);
            if (res?.data) setBadge(res.data.tabsWithContent || 0);
            else setBadge(0);
        } catch (err) {
            console.warn('[useCompanyNotes] fetchBadge failed, setting badge to 0:', err);
            setBadge(0);
        }
    }, [companyId]);

    /* ── Reset fetch tracking when companyId changes ── */
    useEffect(() => {
        if (companyId) {
            fetchedRef.current = null; // Reset to allow fresh fetch
        }
    }, [companyId]);

    /* ── Get active tab object ── */
    const activeTab = tabs.find(t => t.tabId === activeTabId) || tabs[0] || null;

    /* ── Create custom tab ── */
    const addTab = useCallback(async (tabName) => {
        if (!companyId) return;
        try {
            const order = tabs.length + 1;
            const res = await notesApi.createTab(companyId, { tabName, order, companyName });
            if (res?.data) {
                const newTab = { ...res.data, content: { title: '', richContent: '', plainText: '', lastEdited: null } };
                setTabs(prev => [...prev, newTab]);
                setActiveTabId(newTab.tabId);
                toast.success(`Tab "${tabName}" created`, { autoClose: 1500 });
            }
        } catch (err) {
            toast.error('Failed to create tab');
            console.error('[useCompanyNotes] addTab error:', err);
        }
    }, [companyId, companyName, tabs.length]);

    /* ── Delete tab ── */
    const removeTab = useCallback(async (tabId) => {
        if (!companyId) return;
        try {
            const res = await notesApi.deleteTab(companyId, tabId);
            if (res?.data?.success) {
                setTabs(prev => {
                    const filtered = prev.filter(t => t.tabId !== tabId);
                    if (activeTabId === tabId && filtered.length) {
                        setActiveTabId(filtered[0].tabId);
                    }
                    return filtered;
                });
                toast.success('Tab deleted', { autoClose: 1500 });
            } else {
                toast.error(res?.data?.error || 'Cannot delete this tab');
            }
        } catch (err) {
            toast.error('Failed to delete tab');
            console.error('[useCompanyNotes] removeTab error:', err);
        }
    }, [companyId, activeTabId]);

    /* ── Rename tab (local only — persists on next auto-save) ── */
    const renameTab = useCallback((tabId, newName) => {
        setTabs(prev => prev.map(t => t.tabId === tabId ? { ...t, tabName: newName } : t));
    }, []);

    /* ── Update local tab content (optimistic — auto-save handles persistence) ── */
    const updateLocalTabContent = useCallback((tabId, content) => {
        setTabs(prev => prev.map(t =>
            t.tabId === tabId ? { ...t, content: { ...t.content, ...content } } : t
        ));
    }, []);

    /* ── Save from AI ── */
    const saveAIResponse = useCallback(async (tabId, content, aiContext) => {
        if (!companyId) return;
        try {
            const res = await notesApi.saveFromAI(companyId, {
                tabId,
                content,
                appendMode: true,
                aiContext,
                companyName,
            });
            if (res?.data?.success) {
                toast.success('Saved to notes!', { autoClose: 1500 });
                // Refresh to get updated content
                await fetchNotes();
            }
        } catch (err) {
            toast.error('Failed to save to notes');
            console.error('[useCompanyNotes] saveAIResponse error:', err);
        }
    }, [companyId, companyName, fetchNotes]);

    return {
        notes,
        tabs,
        activeTabId,
        setActiveTabId,
        activeTab,
        loading,
        badge,
        fetchNotes,
        fetchBadge,
        addTab,
        removeTab,
        renameTab,
        updateLocalTabContent,
        saveAIResponse,
    };
}
